import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Dna, Cpu, BadgeCheck, Globe, MapPin, FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AssignTestDialog } from "@/components/marketplace/AssignTestDialog";

export default function BrandDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [assign, setAssign] = useState(null);
  const load = useCallback(() => api.get(`/marketplace/brands/${id}`).then((r) => setB(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!b) return <div className="text-sm text-slate-500">Loading…</div>;
  const canAssign = ["admin", "doctor", "staff"].includes(user.role);
  const Icon = b.category === "dx" ? Dna : Cpu;

  return (
    <div className="space-y-6" data-testid="brand-detail-page">
      <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-700" data-testid="back-to-marketplace"><ArrowLeft className="w-4 h-4" /> Marketplace</Link>
      <div className="clinical-card overflow-hidden">
        <div className="h-44 bg-gradient-to-br from-teal-800 to-slate-900 relative">
          {b.banner && <img src={b.banner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />}
        </div>
        <div className="p-6 sm:p-8 -mt-14 relative">
          <div className="flex flex-wrap items-end gap-5">
            <span className="w-24 h-24 rounded-3xl bg-white shadow-lg ring-1 ring-slate-100 grid place-items-center text-teal-800 font-heading font-extrabold text-4xl shrink-0">{b.name?.[0]}</span>
            <div className="flex-1 min-w-[240px]">
              <span className={`badge-pill ${b.category === "dx" ? "bg-teal-50 text-teal-800 border-teal-200" : "bg-indigo-50 text-indigo-800 border-indigo-200"}`}><Icon className="w-3.5 h-3.5" /> {b.category === "dx" ? "Diagnostics" : "Medtech"}</span>
              <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-2" data-testid="brand-name">{b.name}</h1>
              <p className="text-sm text-slate-600 mt-1">{b.tagline}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-5 text-sm text-slate-600">
            {b.city && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {b.city}</span>}
            {b.founded && <span className="flex items-center gap-1.5"><FlaskConical className="w-4 h-4 text-slate-400" /> Est. {b.founded}</span>}
            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-teal-700 hover:underline"><Globe className="w-4 h-4" /> Website</a>}
          </div>
          {b.about && <p className="text-sm text-slate-700 leading-relaxed mt-5 max-w-3xl">{b.about}</p>}
          {b.certifications?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5" data-testid="brand-certifications">
              {b.certifications.map((c) => <span key={c} className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-200"><BadgeCheck className="w-3.5 h-3.5" /> {c}</span>)}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">{b.category === "dx" ? "Tests & panels" : "Devices & solutions"}</h2>
        <div className="grid md:grid-cols-2 gap-4" data-testid="brand-products">
          {b.products?.length === 0 && <p className="text-sm text-slate-500">No products listed yet.</p>}
          {b.products?.map((p) => (
            <div key={p.id} className="clinical-card p-5 flex flex-col" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-semibold text-slate-900">{p.name}</p>
                  {p.category && <p className="eyebrow mt-0.5">{p.category}</p>}
                </div>
                {p.price && <span className="text-sm font-bold text-teal-800 whitespace-nowrap">{p.price}</span>}
              </div>
              {p.description && <p className="text-sm text-slate-600 mt-2 flex-1">{p.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">{p.tags?.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}</div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                {p.sample_type && <span><b className="text-slate-700">Sample:</b> {p.sample_type}</span>}
                {p.turnaround && <span><b className="text-slate-700">TAT:</b> {p.turnaround}</span>}
              </div>
              {canAssign && (
                <button data-testid={`assign-product-${p.id}`} onClick={() => setAssign(p)} className="mt-4 h-9 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold transition-colors">
                  Assign to patient
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {assign && <AssignTestDialog product={assign} brand={b} open={!!assign} onClose={() => setAssign(null)} />}
    </div>
  );
}
